import "./GanttFilters.css";

import { JSX } from "react/jsx-runtime";

import { SelectData } from "../../../../../models";
import { AutocompleteMultiSelect } from "../../../../forms/AutocompleteMultiSelect";
import { Button } from "../../../../forms/Button";
import { MultiSelect } from "../../../../forms/MultiSelect";
import {
  GanttActiveFilters,
  GanttFilterObject,
  GanttFilterOption,
  GanttFiltersProps,
  SearchInputProps,
} from "../../models";

/** Map the chart's `{ label, value }` filter options onto the shared select's `{ key, value }` shape. */
const toSelectData = (options: (GanttFilterOption | GanttFilterObject)[]): SelectData =>
  options.map((o) => ({ key: o.value, value: o.label }));

const SearchInput = ({ value, onChange }: SearchInputProps): JSX.Element => (
  <div className="GanttSearch">
    <svg className="GanttSearchIcon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
    <input
      className="GanttSearchInput body3"
      type="search"
      placeholder="Search tasks"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || undefined)}
      aria-label="Search tasks"
    />
  </div>
);

export const GanttFilters = ({ options, active, onChange }: GanttFiltersProps): JSX.Element => {
  const update = (patch: Partial<GanttActiveFilters>): void => onChange({ ...active, ...patch });

  const hasActiveFilters = active.assignee?.length || active.object?.length || active.status?.length || active.search;

  return (
    <div className="GanttFilters">
      <div className="GanttFilterGroup">
        <SearchInput value={active.search} onChange={(v) => update({ search: v })} />
        <div className="GanttFilterField">
          <MultiSelect
            placeholder="Assignee"
            className="GanttFilterMultiSelect"
            data={toSelectData(options.assignees)}
            values={active.assignee ?? []}
            dropdownMatchSelectWidth={false}
            onChange={(assignee) => update({ assignee: assignee.length ? assignee : undefined })}
          />
        </div>
        <div className="GanttFilterField">
          <AutocompleteMultiSelect
            placeholder="Project / Deal"
            className="GanttFilterMultiSelect"
            data={toSelectData(options.objects)}
            values={active.object ?? []}
            dropdownMatchSelectWidth={false}
            onChange={(object) => update({ object: object.length ? object : undefined })}
          />
        </div>
        {options.statuses && options.statuses.length > 0 && (
          <div className="GanttFilterField">
            <MultiSelect
              placeholder="Status"
              className="GanttFilterMultiSelect"
              data={toSelectData(options.statuses)}
              values={active.status ?? []}
              dropdownMatchSelectWidth={false}
              onChange={(status) => update({ status: status.length ? status : undefined })}
            />
          </div>
        )}
        {hasActiveFilters && (
          <Button
            text="Clear filters"
            variant="secondary"
            size="extraSmall"
            minWidth={false}
            onClick={() => onChange({})}
          />
        )}
      </div>
    </div>
  );
};
